// frontend/src/components/PartnerMessageModal.js
// ✉️ Partner-levél küldése a kiválasztott affiliate partnereknek.
//
// Ugyanazt a /api/email-send/send végpontot használja, mint a POI-usereknek szóló
// levél, de `type: 'affiliate'` jelöléssel — így a napló és a „kinek ment már ki"
// jelzés nem keveredik össze a kettő között.
//
// A partnereknél NINCS nyelv-mező az adatban, ezért a nyelvet itt kézzel kell
// választani. A címzettlista látható, hogy tudj külön kört küldeni a magyar és a
// külföldi partnereknek.
import React, { useMemo, useState } from 'react';

const C = {
  bg: '#1a1d27', panel: '#22252f', border: '#2a2e3a', text: '#e0e6ed',
  muted: '#8892a0', accent: '#00d4ff', warn: '#fbbf24', ok: '#22c55e', bad: '#ef4444',
};

const FROM = 'AI TourMate <support@aitourmate.eu>';

export default function PartnerMessageModal({ partners, templates, suggest, onClose, onSent }) {
  // A legtöbb kijelölt partnerhez illő sablon az alapértelmezés.
  const defaultKey = useMemo(() => {
    const counts = {};
    partners.forEach((p) => { const k = suggest(p); counts[k] = (counts[k] || 0) + 1; });
    return Object.entries(counts).sort((a, b) => b[1] - a[1])[0]?.[0] || 'restart';
  }, [partners, suggest]);

  const [tplKey, setTplKey] = useState(defaultKey);
  const [lang, setLang] = useState('hu');
  const [subject, setSubject] = useState(templates[defaultKey].hu.subject);
  const [body, setBody] = useState(templates[defaultKey].hu.bodyText);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [edited, setEdited] = useState(false);

  const applyTemplate = (key, l) => {
    setTplKey(key); setLang(l);
    // A kézi szerkesztést nem írjuk felül kérdés nélkül.
    if (edited && !window.confirm('A szerkesztett szöveg elveszik. Betöltsem a sablont?')) return;
    setSubject(templates[key][l].subject);
    setBody(templates[key][l].bodyText);
    setEdited(false);
  };

  // Előnézet az ELSŐ címzettel, hogy lásd, mit kap valójában.
  const preview = useMemo(() => {
    const p = partners[0];
    if (!p) return { subject, body };
    const first = (p.name || '').trim().split(/\s+/)[0] || (lang === 'hu' ? 'Partner' : 'there');
    const sub = (v) => String(v)
      .replace(/\{\{\s*name\s*\}\}/g, first)
      .replace(/\{\{\s*code\s*\}\}/g, p.code || '')
      .replace(/\{\{\s*total\s*\}\}/g, p.total != null ? p.total : 0)
      .replace(/\{\{\s*new30\s*\}\}/g, p.new30 != null ? p.new30 : 0);
    return { subject: sub(subject), body: sub(body) };
  }, [partners, subject, body, lang]);

  const send = async () => {
    if (!window.confirm(`Biztosan kimegy a levél ${partners.length} partnernek?`)) return;
    setSending(true); setResult(null);
    try {
      const r = await fetch('/api/email-send/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          confirm: true,
          type: 'affiliate',
          from: FROM,
          subject,
          bodyText: body,
          recipients: partners.map((p) => ({
            email: p.email,
            name: p.name,
            vars: { code: p.code, total: p.total || 0, new30: p.new30 || 0 },
          })),
        }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || 'HTTP ' + r.status);
      setResult({ ok: true, ...j });
    } catch (e) {
      setResult({ ok: false, error: e.message });
    } finally { setSending(false); }
  };

  const box = {
    width: '100%', background: C.bg, border: '1px solid ' + C.border, borderRadius: 8,
    color: C.text, padding: 10, fontSize: 13, boxSizing: 'border-box', fontFamily: 'inherit',
  };
  const chip = (on) => ({
    padding: '6px 12px', borderRadius: 8, fontSize: 12, cursor: 'pointer',
    border: '1px solid ' + (on ? C.accent : C.border),
    background: on ? C.accent : C.bg, color: on ? '#0a0e1a' : C.text, fontWeight: on ? 700 : 400,
  });

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000,
      display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: 24, overflowY: 'auto',
    }} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: C.panel, border: '1px solid ' + C.border, borderRadius: 12,
        padding: 20, width: '100%', maxWidth: 760,
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <div style={{ fontSize: 17, fontWeight: 700, color: C.text }}>
            ✉️ Üzenet {partners.length} partnernek
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: C.muted, fontSize: 20, cursor: 'pointer' }}>×</button>
        </div>

        {/* Címzettek — látni kell, ki kapja, mielőtt nyelvet választasz */}
        <div style={{ fontSize: 12, color: C.muted, marginBottom: 12, maxHeight: 88, overflowY: 'auto',
          background: C.bg, border: '1px solid ' + C.border, borderRadius: 8, padding: 8 }}>
          {partners.map((p) => (
            <span key={p.code} style={{ marginRight: 10, whiteSpace: 'nowrap' }}>
              <b style={{ color: C.accent }}>{p.code}</b> {p.name} <span style={{ color: '#5b6270' }}>&lt;{p.email}&gt;</span>
            </span>
          ))}
        </div>

        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
          {Object.entries(templates).map(([k, t]) => (
            <span key={k} onClick={() => applyTemplate(k, lang)} style={chip(tplKey === k)} title={t.hint}>
              {t.label}
            </span>
          ))}
          <span style={{ width: 12 }} />
          {['hu', 'en'].map((l) => (
            <span key={l} onClick={() => applyTemplate(tplKey, l)} style={chip(lang === l)}>
              {l === 'hu' ? '🇭🇺 Magyar' : '🇬🇧 English'}
            </span>
          ))}
        </div>
        <div style={{ fontSize: 11, color: C.warn, marginBottom: 12, lineHeight: 1.5 }}>
          A partnereknél nincs nyelv az adatban, ezért a nyelvet neked kell választanod.
          Ha vegyes a lista, küldj két kört — a címzettek fent láthatók.
        </div>

        <input value={subject} onChange={(e) => { setSubject(e.target.value); setEdited(true); }}
          placeholder="Tárgy" style={{ ...box, marginBottom: 8 }} />
        <textarea value={body} onChange={(e) => { setBody(e.target.value); setEdited(true); }}
          style={{ ...box, minHeight: 220, resize: 'vertical', lineHeight: 1.5 }} />
        <div style={{ fontSize: 11, color: C.muted, margin: '6px 0 12px' }}>
          Behelyettesíthető: <code>{'{{name}}'}</code> (keresztnév) · <code>{'{{code}}'}</code> ·
          {' '}<code>{'{{total}}'}</code> · <code>{'{{new30}}'}</code>
        </div>

        {partners[0] && (
          <details style={{ marginBottom: 12 }}>
            <summary style={{ cursor: 'pointer', fontSize: 12, color: C.accent }}>
              Előnézet — amit {partners[0].name} kap
            </summary>
            <div style={{ background: C.bg, border: '1px solid ' + C.border, borderRadius: 8, padding: 12, marginTop: 8 }}>
              <div style={{ fontWeight: 700, color: C.text, fontSize: 13, marginBottom: 8 }}>{preview.subject}</div>
              <div style={{ color: C.muted, fontSize: 12, whiteSpace: 'pre-wrap', lineHeight: 1.55 }}>{preview.body}</div>
            </div>
          </details>
        )}

        {result && (
          <div style={{
            marginBottom: 12, padding: 10, borderRadius: 8, fontSize: 12,
            border: '1px solid ' + (result.ok ? C.ok : C.bad), color: result.ok ? C.ok : C.bad,
          }}>
            {result.ok
              // A végpont `success`-t ad vissza, nem `sent`-et — korábban emiatt
              //   „Elküldve: ?" jelent meg, vagyis nem lehetett látni, tényleg
              //   kiment-e mindenkinek.
              ? `✅ Elküldve: ${result.success} / ${result.total}` +
                (result.failed ? ` · ${result.failed} sikertelen` : '')
              : `❌ ${result.error}`}
            {result.ok && (
              <div style={{ color: C.muted, marginTop: 6 }}>
                A küldés naplózva — az Admin oldalon és a partnerek melletti ✉️ jelzésen is látszik.
              </div>
            )}
            {/* Ha valakinek nem ment ki, tudni kell, KINEK — különben azt hinnéd, mind megkapta. */}
            {result.ok && result.failed > 0 && (
              <div style={{ color: C.bad, marginTop: 6 }}>
                Nem ment ki: {(result.results || []).filter((x) => !x.ok)
                  .map((x) => `${x.email} (${x.error})`).join(', ')}
              </div>
            )}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
          <div style={{ fontSize: 11, color: C.muted }}>Feladó: {FROM}</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={result && result.ok ? onSent : onClose}
              style={{ padding: '9px 16px', borderRadius: 8, border: '1px solid ' + C.border, background: C.bg, color: C.text, cursor: 'pointer', fontSize: 13 }}>
              {result && result.ok ? 'Bezárás' : 'Mégsem'}
            </button>
            <button onClick={send} disabled={sending || !partners.length || (result && result.ok)}
              style={{
                padding: '9px 18px', borderRadius: 8, border: 'none', fontWeight: 700, fontSize: 13,
                background: sending || (result && result.ok) ? C.border : C.accent,
                color: sending || (result && result.ok) ? C.muted : '#0a0e1a',
                cursor: sending || (result && result.ok) ? 'default' : 'pointer',
              }}>
              {sending ? '⏳ Küldés…' : `Küldés (${partners.length})`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
